import type { RazorpayVBankPayment } from '../types'

// Credit-detection helpers for the Razorpay bank-transfer drawer's poll. Kept
// out of the component file so they stay unit-testable and Fast Refresh can
// still preserve the drawer's state (a module mixing components with plain
// exports opts out of it).
//
// Razorpay payment statuses: created / authorized / captured / refunded /
// failed. Only captured has credited the account and only failed is a terminal
// rejection; the rest are still in flight.

const TERMINAL_STATUSES = new Set(['captured', 'failed'])

// Ids to seed the baseline from the first poll while the drawer is open.
// Terminal payments are history the customer has already been told about, so
// they must not toast on the next tick. In-flight ones are deliberately left
// out, so their transition into captured / failed is still caught later.
export const baselinePaymentIds = (payments: RazorpayVBankPayment[]): Set<string> =>
  new Set(payments.filter((p) => TERMINAL_STATUSES.has(p.status)).map((p) => p.payment_id))

// A newly-settled inbound credit: not in the baseline and captured.
export const findNewCapturedPayment = (
  payments: RazorpayVBankPayment[],
  baselineIds: Set<string>,
): RazorpayVBankPayment | undefined =>
  payments.find((p) => !baselineIds.has(p.payment_id) && p.status === 'captured')

// Mirrors the Cashfree drawer's FAILED branch: a credit that came back rejected
// is worth telling the user about, and joins the baseline so it toasts once.
export const findNewFailedPayment = (
  payments: RazorpayVBankPayment[],
  baselineIds: Set<string>,
): RazorpayVBankPayment | undefined =>
  payments.find((p) => !baselineIds.has(p.payment_id) && p.status === 'failed')
